/**
 * FLAWLESS TESTIMONIALS SLIDER
 * Perfect UX, accessibility, and performance
 */

class FlawlessTestimonialsSlider {
  constructor() {
    this.slider = null;
    this.slides = [];
    this.prevButton = null;
    this.nextButton = null;
    this.paginationContainer = null;
    this.currentSlide = 0;
    this.totalSlides = 0;
    this.autoSlideInterval = null;
    this.isTransitioning = false;
    this.isUserInteracting = false;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.animationFrame = null;
    this.visibilityObserver = null;

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.slider = document.querySelector('.testimonials-slider');
    this.slides = Array.from(document.querySelectorAll('.testimonial-slide'));
    this.prevButton = document.querySelector('.testimonial-prev');
    this.nextButton = document.querySelector('.testimonial-next');
    this.paginationContainer = document.querySelector(
      '.testimonials-pagination'
    );

    if (!this.slider || this.slides.length === 0) {
      return;
    }

    this.totalSlides = this.slides.length;
    this.setupAccessibility();
    this.createPaginationDots();
    this.setupEventListeners();
    this.updateSlideVisibility();
    this.startAutoSlide();
    this.setupIntersectionObserver();
  }

  setupAccessibility() {
    // Add ARIA attributes
    this.slider.setAttribute('role', 'region');
    this.slider.setAttribute('aria-label', 'Customer testimonials carousel');
    this.slider.setAttribute('aria-live', 'polite');

    // Add keyboard navigation
    this.slider.setAttribute('tabindex', '0');

    // Update slide ARIA attributes
    this.slides.forEach((slide, index) => {
      slide.setAttribute('role', 'tabpanel');
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      slide.setAttribute(
        'aria-label',
        `Testimonial ${index + 1} of ${this.totalSlides}`
      );
    });
  }

  createPaginationDots() {
    if (!this.paginationContainer) return;

    this.paginationContainer.innerHTML = '';
    this.paginationContainer.setAttribute('role', 'tablist');
    this.paginationContainer.setAttribute(
      'aria-label',
      'Testimonial navigation'
    );

    this.slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.classList.add('pagination-dot');
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to testimonial ${index + 1}`);
      dot.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      dot.setAttribute('tabindex', index === 0 ? '0' : '-1');

      dot.addEventListener('click', e => {
        e.preventDefault();
        this.goToSlide(index);
      });

      dot.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.goToSlide(index);
        }
      });

      this.paginationContainer.appendChild(dot);
    });
  }

  setupEventListeners() {
    // Navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', e => {
        e.preventDefault();
        this.prevSlide();
      });

      this.prevButton.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.prevSlide();
        }
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', e => {
        e.preventDefault();
        this.nextSlide();
      });

      this.nextButton.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.nextSlide();
        }
      });
    }

    // Keyboard navigation
    this.slider.addEventListener('keydown', e => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.prevSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextSlide();
          break;
        case 'Home':
          e.preventDefault();
          this.goToSlide(0);
          break;
        case 'End':
          e.preventDefault();
          this.goToSlide(this.totalSlides - 1);
          break;
      }
    });

    // Touch gestures
    this.slider.addEventListener(
      'touchstart',
      e => {
        this.touchStartX = e.touches[0].clientX;
        this.isUserInteracting = true;
        this.pauseAutoSlide();
      },
      { passive: true }
    );

    this.slider.addEventListener(
      'touchmove',
      e => {
        if (this.isUserInteracting) {
          e.preventDefault();
        }
      },
      { passive: false }
    );

    this.slider.addEventListener(
      'touchend',
      e => {
        if (!this.isUserInteracting) return;

        this.touchEndX = e.changedTouches[0].clientX;
        this.handleSwipe();
        this.isUserInteracting = false;
        this.resumeAutoSlide();
      },
      { passive: true }
    );

    // Mouse events
    this.slider.addEventListener('mouseenter', () => {
      this.pauseAutoSlide();
    });

    this.slider.addEventListener('mouseleave', () => {
      this.resumeAutoSlide();
    });

    // Focus management
    this.slider.addEventListener('focus', () => {
      this.pauseAutoSlide();
    });

    this.slider.addEventListener('blur', () => {
      this.resumeAutoSlide();
    });

    // Window events
    window.addEventListener(
      'resize',
      this.debounce(() => {
        this.updateSlideVisibility();
      }, 250)
    );

    window.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoSlide();
      } else {
        this.resumeAutoSlide();
      }
    });
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
    }
  }

  goToSlide(slideIndex) {
    if (this.isTransitioning || slideIndex === this.currentSlide) return;

    this.isTransitioning = true;
    this.pauseAutoSlide();

    const direction = slideIndex > this.currentSlide ? 'next' : 'prev';
    this.animateSlideTransition(slideIndex, direction);
  }

  nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.totalSlides;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex =
      (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
    this.goToSlide(prevIndex);
  }

  animateSlideTransition(targetSlide, direction) {
    const currentSlideElement = this.slides[this.currentSlide];
    const targetSlideElement = this.slides[targetSlide];

    // Add transition classes
    currentSlideElement.classList.add('slide-exit');
    targetSlideElement.classList.add('slide-enter');

    if (direction === 'next') {
      currentSlideElement.classList.add('slide-exit-left');
      targetSlideElement.classList.add('slide-enter-right');
    } else {
      currentSlideElement.classList.add('slide-exit-right');
      targetSlideElement.classList.add('slide-enter-left');
    }

    // Update current slide
    this.currentSlide = targetSlide;

    // Use requestAnimationFrame for smooth animation
    this.animationFrame = requestAnimationFrame(() => {
      setTimeout(() => {
        this.updateSlideVisibility();
        this.cleanupTransitionClasses();
        this.isTransitioning = false;
        this.resumeAutoSlide();
      }, 300);
    });
  }

  cleanupTransitionClasses() {
    this.slides.forEach(slide => {
      slide.classList.remove(
        'slide-exit',
        'slide-enter',
        'slide-exit-left',
        'slide-exit-right',
        'slide-enter-left',
        'slide-enter-right'
      );
    });
  }

  updateSlideVisibility() {
    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentSlide;

      slide.classList.toggle('active', isActive);
      slide.style.display = isActive ? 'flex' : 'none';
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    // Update pagination dots
    const dots = this.paginationContainer?.querySelectorAll('.pagination-dot');
    dots?.forEach((dot, index) => {
      const isActive = index === this.currentSlide;
      dot.classList.toggle('active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      dot.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // Update navigation buttons
    if (this.prevButton) {
      this.prevButton.disabled = false; // Always enabled for infinite loop
      this.prevButton.setAttribute(
        'aria-label',
        `Previous testimonial (currently showing ${this.currentSlide + 1} of ${this.totalSlides})`
      );
    }

    if (this.nextButton) {
      this.nextButton.disabled = false; // Always enabled for infinite loop
      this.nextButton.setAttribute(
        'aria-label',
        `Next testimonial (currently showing ${this.currentSlide + 1} of ${this.totalSlides})`
      );
    }
  }

  startAutoSlide() {
    this.autoSlideInterval = setInterval(() => {
      if (!this.isUserInteracting && !this.isTransitioning) {
        this.nextSlide();
      }
    }, 5000);
  }

  pauseAutoSlide() {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
      this.autoSlideInterval = null;
    }
  }

  resumeAutoSlide() {
    if (!this.autoSlideInterval && !this.isUserInteracting) {
      this.startAutoSlide();
    }
  }

  setupIntersectionObserver() {
    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.resumeAutoSlide();
            } else {
              this.pauseAutoSlide();
            }
          });
        },
        { threshold: 0.5 }
      );

      this.visibilityObserver.observe(this.slider);
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  destroy() {
    this.pauseAutoSlide();

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }

    // Remove event listeners
    this.slider?.removeEventListener('keydown', this.handleKeydown);
    this.slider?.removeEventListener('touchstart', this.handleTouchStart);
    this.slider?.removeEventListener('touchmove', this.handleTouchMove);
    this.slider?.removeEventListener('touchend', this.handleTouchEnd);
    this.slider?.removeEventListener('mouseenter', this.handleMouseEnter);
    this.slider?.removeEventListener('mouseleave', this.handleMouseLeave);
  }
}

// Initialize the flawless slider
document.addEventListener('DOMContentLoaded', () => {
  new FlawlessTestimonialsSlider();
});

// Also initialize on window load as fallback
window.addEventListener('load', () => {
  if (!document.querySelector('.testimonials-slider .active')) {
    new FlawlessTestimonialsSlider();
  }
});
